import React, { useContext, useMemo } from 'react';

import { Box } from '@material-ui/core';
import { Redirect, Route, Switch, useHistory, useParams } from 'react-router-dom';

import { DeviceContext } from '@vyce/core/src/contexts/deviceContext';
import { CommonNavBarBlock } from '@vyce/core/src/components';
import { PreviewButton } from '@vyce/core/src/views/preview/components/PreviewButton';
import { COMPANY_PROFILE_TABS } from '@vyce/core/src/views/profile/config';
import { COMPANY_PERMISSIONS, INTERFACE_PERMISSIONS, PAY_PERMISSIONS } from '@vyce/core/src/types';
import { hasCompanyPermission } from '@vyce/core/src/utils/permissions';
import { isOutsourcedOnlyCompany } from '@vyce/core/src/utils/payroll';
import { getUrlItems } from '@vyce/core/src/utils/url';

import { useTypedSelector } from 'src/hooks';
import { RouteCompanyProfileComponent } from './RouteCompanyProfileComponent';

export const CompanyProfile: React.FC = () => {
  const { isMobile } = useContext(DeviceContext);
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { positions } = useTypedSelector(state => state.user);

  // This page can be opened for any company the user belongs to, not just the
  // selected one, so the check runs against that company's own position.
  const companyId = getUrlItems(id)?.id;
  const position = useMemo(
    () => positions?.find(item => item.company?.uuid === companyId),
    [positions, companyId]
  );

  const canEdit = hasCompanyPermission(position, COMPANY_PERMISSIONS.CAN_EDIT);
  // This page is addressed by company id, not by the selected one, so the flags come
  // from that company's position rather than from usePermissions. An outsourced-only
  // company has no account to pay from — Vyce pays the workers.
  const outsourcedOnlyCompany = isOutsourcedOnlyCompany(undefined, {
    canAccessPayroll: hasCompanyPermission(position, INTERFACE_PERMISSIONS.PAYROLL),
    canAccessPayrollOutsourced: hasCompanyPermission(position, INTERFACE_PERMISSIONS.PAYROLL_OUTSOURCED),
    isOwner: !!position?.owner,
  });
  const canManageBankDetails =
    hasCompanyPermission(position, PAY_PERMISSIONS.MANAGE_BANK_DETAILS) && !outsourcedOnlyCompany;

  const tabs = useMemo(
    () => COMPANY_PROFILE_TABS.filter(tab => tab.link !== 'bank' || canManageBankDetails),
    [canManageBankDetails]
  );

  const goToPreview = () => {
    history.push(`/companies/${id}`);
  };

  // The whole page is the edit surface; the read-only view lives at /companies/:id.
  if (!canEdit) {
    return <Redirect to={`/companies/${id}`} />;
  }

  return (
    <Box position="relative">
      <CommonNavBarBlock tabItems={tabs} />

      <PreviewButton goToPreview={goToPreview} />

      <Box padding={isMobile ? 1 : 5} paddingBottom={12} height="100%">
        <Switch>
          <Route component={RouteCompanyProfileComponent} />
        </Switch>
      </Box>
    </Box>
  );
};
